import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { SetURLSearchParams, useNavigate, useSearchParams } from 'react-router-dom';

import css from './style.module.scss';

import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Headline from '@sv-support-sdk/ui-components/src/components/Typography/Headline';
import Paragraph from '@sv-support-sdk/ui-components/src/components/Typography/Paragraph';
import Actions from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions';
import ActionsItem from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions/ActionsItem';
import { notificationsActions } from '@sv-support-sdk/store';

import PageContentInner from '../../components/Layout/Page/PageContentInner';
import Button from '../../components/Controls/Button';
import Textarea from '../../components/Controls/Textarea';
import Rating from '../../components/Feedback/Rating';

import { useAppDispatch } from '../../store/hooks';
import { DEFAULT_RATE } from '../../constants/customerEffort';
import { routes } from '../../routing';
import { $l } from '../../locales';
import api from '../../api';

const CustomerEffortScore = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams();
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const s = searchParams.get('s');
  const i = Number(searchParams.get('i'));
  const h = searchParams.get('h');

  const disabled = !message || !message.trim() || loading;

  const score = useMemo(() => {
    const validRate = /^[1-5]$/;

    return s && validRate.test(s) ? +s : DEFAULT_RATE;
  }, [s]);

  const changeMessage = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(value);
  };

  const sendFeedback = useCallback(async () => {
    setLoading(true);

    try {
      const { ok } = await api.customerEffort.sendTicketCES({
        i,
        h,
        s: score,
        c: message && message.trim(),
      });

      if (ok) {
        setMessage('');
        dispatch(
          notificationsActions.notifySuccess({
            title: $l('CUSTOMER_EFFORT.THANK_YOU'),
            message: $l('CUSTOMER_EFFORT.WE_APPRECIATE'),
          }),
        );
        navigate(routes.$root);
      } else {
        dispatch(
          notificationsActions.notifyError({
            title: $l('CUSTOMER_EFFORT.OOPS'),
            message: $l('CUSTOMER_EFFORT.SOMETHING_WENT_WRONG'),
          }),
        );
      }
    } catch (e) {
      dispatch(
        notificationsActions.notifyError({
          title: $l('CUSTOMER_EFFORT.OOPS'),
          message: $l('CUSTOMER_EFFORT.SOMETHING_WENT_WRONG'),
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [score, i, h, message, dispatch]);

  useEffect(() => {
    api.customerEffort.sendTicketCES({ i, h, s: score }).catch(() => {});
  }, [h, i, score]);

  return (
    <PageContentInner height="full" align="center">
      <div className={css.customerEffort}>
        <List gap="16" width="full">
          {/*@ts-ignore*/}
          <Rating rate={score} viewOnly type="customerEffort" gap="4" />
          <Headline type="h4">
            {/*@ts-ignore*/}
            {$l(`CUSTOMER_EFFORT.TITLE_${score}`)}
          </Headline>
          <Paragraph fontSize="16" lineHeight="24" letterSpacing="0_15">
            {/*@ts-ignore*/}
            {$l(`CUSTOMER_EFFORT.DESCRIPTION_${score}`)}
          </Paragraph>
          <Textarea
            value={message}
            onChange={changeMessage}
            height="96"
            placeholder={$l('CUSTOMER_EFFORT.TELL_US')}
            autoFocus
          />
          <Actions align="center">
            <ActionsItem width="149">
              <Button
                text={$l('CUSTOMER_EFFORT.LEAVE_FEEDBACK')}
                isDisabled={disabled}
                onClick={sendFeedback}
              />
            </ActionsItem>
          </Actions>
        </List>
      </div>
    </PageContentInner>
  );
};

export default CustomerEffortScore;
